import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useQueryState, { getQueryStringValue } from "../../../hooks/useQueryState";
import { useOrganisation } from "../../../networking/organisation.hooks";
import {
	useCurrentUser,
	UserPreferences,
	useUpdateMyPreferences,
} from "../../../networking/user.hooks";
import { SavedFilter } from "./types";

/**
 * Configuration for {@link useListFilters}. A domain plugs in the key its
 * filters live under (the same key is used on both the user's preferences and
 * the organisation) plus its definition's empty/equality helpers.
 */
interface UseListFiltersConfig<TDef> {
	/**
	 * Key that stores this domain's saved filters under both
	 * `user.profile.preferences` and `organisation` (e.g. "jobFilters").
	 */
	filtersKey: keyof UserPreferences;
	/** Whether two definitions are equivalent (used to match the active pill). */
	definitionsEqual: (a: TDef, b: TDef) => boolean;
	/** Whether a definition has no active constraints (the "All" baseline). */
	isEmptyDefinition: (def: TDef) => boolean;
	/** The empty definition, used as the baseline and when clearing with no base. */
	emptyDefinition: TDef;
}

/** Everything a list page (and its toolbar/panel) needs to drive filtering. */
export interface ListFilters<TDef> {
	/** Free-text search term (URL-backed via ?search=). Debounce at the call site. */
	searchTerm: string;
	setSearchTerm: (value: string) => void;
	/** Pool scope (URL-backed via ?pool=) — the breadcrumb pool dropdown. */
	pool: string;
	setPool: (value: string) => void;
	/** The active filter definition (URL-backed via ?filter=<json>). */
	definition: TDef;
	setDefinition: (def: TDef) => void;
	/** The current user's personal saved filters. */
	savedFilters: SavedFilter<TDef>[];
	/** Organisation-defined, apply-only filters. */
	orgFilters: SavedFilter<TDef>[];
	selectedSavedId?: string;
	/** True while the active definition still equals the selected personal filter. */
	matchesSelectedSaved: boolean;
	/** True when the definition matches neither an org filter nor the selected saved one. */
	isCustom: boolean;
	/** True when the user has built a non-empty custom filter (shows count + clear). */
	hasActiveFilter: boolean;
	/** Whether an org filter pill should render as active. */
	isOrgFilterActive: (filter: SavedFilter<TDef>) => boolean;
	/** Whether a personal saved-filter pill should render as active. */
	isSavedFilterActive: (filter: SavedFilter<TDef>) => boolean;
	applyDefinition: (def: TDef) => void;
	clearFilter: () => void;
	selectSavedFilter: (saved: SavedFilter<TDef>) => void;
	handleSaveNew: (name: string, def: TDef) => void;
	handleRenameSaved: (id: string, name: string) => void;
	handleUpdateSaved: (id: string, def: TDef) => void;
	handleReorderSaved: (ordered: SavedFilter<TDef>[]) => void;
	handleDeleteSaved: (id: string) => void;
}

function parseDefinition<TDef>(raw: string | undefined, fallback: TDef): TDef {
	if (!raw) return fallback;
	try {
		return JSON.parse(raw) as TDef;
	} catch {
		return fallback;
	}
}

/**
 * The shared filter state machine for a virtualized list page (jobs, customers,
 * consultants, …). Owns the active definition, the two filter tiers
 * (organisation-defined + personal saved) and every action over them, plus the
 * URL-backed search/pool scope.
 *
 * Generic over the definition type and parameterized by the domain's
 * empty/equality helpers and the preferences key — mirroring how
 * {@link ListFilterPanel} is parameterized — so each list page is reduced to its
 * columns, row renderer and data source.
 *
 *  - Active definition, search and pool live in the URL so links and reloads are
 *    stable; the definition is memo-parsed for a stable object identity.
 *  - Personal saved filters are read from / written to the user's preferences;
 *    org filters are read from the preloaded organisation.
 *  - On load the first org filter becomes the default (unless the user arrived
 *    via an explicit ?filter= link), and the selected personal filter is
 *    re-derived from the restored definition.
 */
export function useListFilters<TDef>({
	filtersKey,
	definitionsEqual,
	isEmptyDefinition,
	emptyDefinition,
}: UseListFiltersConfig<TDef>): ListFilters<TDef> {
	const [searchTerm, setSearchTerm] = useQueryState<string>("search", { initialValue: "" });
	const [pool, setPool] = useQueryState<string>("pool", { initialValue: "" });

	// The active filter definition lives in the URL (?filter=<json>). We keep the
	// serialized string in query state and memo-parse it so the object identity is
	// stable across renders. With nothing in the URL the baseline is the empty
	// definition; once the org loads we auto-select the first org filter (below).
	const [definitionStr, setDefinitionStr] = useQueryState<string>("filter");
	const definition = useMemo(
		() => parseDefinition(definitionStr, emptyDefinition),
		// emptyDefinition is a stable module constant; depend only on the raw string.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[definitionStr]
	);
	const setDefinition = useCallback(
		(def: TDef) => setDefinitionStr(JSON.stringify(def)),
		[setDefinitionStr]
	);

	// Personal saved filters live on the current user's profile.preferences.
	const { data: currentUser } = useCurrentUser();
	const savedFilters = (currentUser?.profile?.preferences?.[filtersKey] ??
		[]) as SavedFilter<TDef>[];
	const { mutate: updatePreferences } = useUpdateMyPreferences();
	const persistSavedFilters = useCallback(
		(next: SavedFilter<TDef>[]) =>
			updatePreferences({ [filtersKey]: next } as Partial<UserPreferences>),
		[updatePreferences, filtersKey]
	);

	// Organisation-defined filters are the primary (apply-only) tier, read from
	// the preloaded org.
	const { data: organisation } = useOrganisation();
	const orgFilters = ((organisation as Record<string, unknown> | undefined)?.[filtersKey] ??
		[]) as SavedFilter<TDef>[];

	// Which saved filter is the editing base (so "Update" targets it after edits).
	const [selectedSavedId, setSelectedSavedId] = useState<string | undefined>(undefined);

	// The predefined org filter the user was last on, so clearing a custom tweak
	// reverts here rather than to all rows. Empty = no base.
	const [baseDefinition, setBaseDefinition] = useState<TDef>(emptyDefinition);

	// The first org filter is the "standard" default: once the org loads, select
	// it unless the user arrived via an explicit ?filter= link. Orgs with no
	// filters stay on the empty definition (all rows). Runs once.
	const appliedDefaultRef = useRef(false);
	useEffect(() => {
		if (appliedDefaultRef.current || organisation === undefined) return;
		appliedDefaultRef.current = true;
		if (getQueryStringValue("filter")) return;
		if (orgFilters.length) setDefinition(orgFilters[0].definition);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [organisation]);

	const matchedOrgFilter = orgFilters.find((f) => definitionsEqual(f.definition, definition));
	const selectedSaved = savedFilters.find((s) => s.id === selectedSavedId);
	// A selected personal filter counts as matched only while the definition still
	// equals it — tweaking it in the panel diverges and makes the filter custom.
	const matchesSelectedSaved =
		!!selectedSaved && definitionsEqual(selectedSaved.definition, definition);
	const isCustom = !matchedOrgFilter && !matchesSelectedSaved;

	// On reload the definition is restored from ?filter= but selectedSavedId is
	// not. Once the user's saved filters load, re-select the one matching the
	// current definition (unless it's an org filter) so its pill shows as active
	// again. Runs once.
	const restoredSavedRef = useRef(false);
	useEffect(() => {
		if (restoredSavedRef.current || currentUser === undefined) return;
		restoredSavedRef.current = true;
		if (selectedSavedId || matchedOrgFilter) return;
		const match = savedFilters.find((s) => definitionsEqual(s.definition, definition));
		if (match) setSelectedSavedId(match.id);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentUser]);

	// Remember the org filter we're sitting on while it's still matched; once the
	// user tweaks it into a custom definition the match drops and this keeps the
	// last one, so "clear" can revert to it.
	useEffect(() => {
		if (matchedOrgFilter) setBaseDefinition(matchedOrgFilter.definition);
	}, [matchedOrgFilter]);

	const applyDefinition = (def: TDef) => {
		setSelectedSavedId(undefined);
		setDefinition(def);
	};

	// The filter button shows its active state (count + clear) only when the user
	// has built a custom filter. Clearing reverts to the filter the tweak started
	// from: the selected personal filter if one is set, else the base org filter,
	// else all rows.
	const hasActiveFilter = isCustom && !isEmptyDefinition(definition);
	const clearFilter = () => {
		if (selectedSaved) setDefinition(selectedSaved.definition);
		else applyDefinition(baseDefinition);
	};

	const selectSavedFilter = (saved: SavedFilter<TDef>) => {
		setSelectedSavedId(saved.id);
		setDefinition(saved.definition);
	};

	const handleSaveNew = (name: string, def: TDef) => {
		const id = `${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
		persistSavedFilters([...savedFilters, { id, name, definition: def }]);
		setSelectedSavedId(id);
	};

	const handleRenameSaved = (id: string, name: string) => {
		persistSavedFilters(savedFilters.map((s) => (s.id === id ? { ...s, name } : s)));
	};

	const handleUpdateSaved = (id: string, def: TDef) => {
		persistSavedFilters(savedFilters.map((s) => (s.id === id ? { ...s, definition: def } : s)));
		setSelectedSavedId(id);
	};

	const handleReorderSaved = (ordered: SavedFilter<TDef>[]) => persistSavedFilters(ordered);

	const handleDeleteSaved = (id: string) => {
		persistSavedFilters(savedFilters.filter((s) => s.id !== id));
		if (selectedSavedId === id) {
			setSelectedSavedId(undefined);
			setDefinition(orgFilters[0]?.definition ?? emptyDefinition);
		}
	};

	const isOrgFilterActive = (filter: SavedFilter<TDef>) =>
		!selectedSavedId && definitionsEqual(filter.definition, definition);

	const isSavedFilterActive = (filter: SavedFilter<TDef>) =>
		selectedSavedId === filter.id && matchesSelectedSaved;

	return {
		searchTerm: searchTerm || "",
		setSearchTerm,
		pool: pool || "",
		setPool,
		definition,
		setDefinition,
		savedFilters,
		orgFilters,
		selectedSavedId,
		matchesSelectedSaved,
		isCustom,
		hasActiveFilter,
		isOrgFilterActive,
		isSavedFilterActive,
		applyDefinition,
		clearFilter,
		selectSavedFilter,
		handleSaveNew,
		handleRenameSaved,
		handleUpdateSaved,
		handleReorderSaved,
		handleDeleteSaved,
	};
}
